import { useEffect, useRef, useState } from "react";

import { ArrowClockwiseIcon } from "@phosphor-icons/react";
import {
  Chart as ChartDotJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  ScatterController,
  type ScriptableContext,
  TimeScale,
  Title,
  Tooltip,
} from "chart.js";
import moment from "moment-timezone";
import { Chart as ChartJS2 } from "react-chartjs-2";

import { Badge, Button, clsx, Spinner } from "@alpacahq/ui/old";

import { useGetMarketStatus, useGetPortfolioHistory } from "~/v2/api/hooks";
import type { Margin } from "~/v2/api/rest/account";
import { ErrorBoundary } from "~/v2/components/ErrorBoundary";
import { useAccountContext } from "~/v2/providers/AccountProvider";
import { Card } from "../../../../../../v2/components";
import { Crosshair } from "./crosshair";
import DeleteAccountModal from "./DeleteAccountModal";
import { Snapshot } from "./snapshot";
import {
  buildChartData,
  denormalizeEquityValue as denormalizeEquity,
} from "./utils";

// register Chart.js components that will be used in the chart
ChartDotJS.register(
  Filler,
  LinearScale,
  ScatterController,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  TimeScale,
  Legend,
);

// define constant values for the available timeframes
const timeframes = ["1D", "1W", "1M", "1A", "all"] as const;

// define a type for the available timeframes and their labels
export type Timeframe = (typeof timeframes)[number];

type TimeframePeriod = {
  label: string;
  value: Timeframe;
};

// define an array of available timeframes and their labels
const TIMEFRAME_PERIODS: TimeframePeriod[] = [
  { label: "1D", value: "1D" },
  { label: "1M", value: "1M" },
  { label: "1Y", value: "1A" },
  { label: "All", value: "all" },
];

// function to abbreviate large numbers
const abbreviateNumber = (value: number): string => {
  // store the initial value
  let newValue = value;

  // check if value is greater than or equal to 1000
  if (value >= 1000) {
    // define suffixes for different magnitudes
    const suffixes = ["", "k", "M", "B", "T"];
    let suffixNum = 0;

    // divide value by 1000 until it's less than 1000, and count the divisions
    while (value / 1000 >= 1) {
      value /= 1000;
      suffixNum++;
    }

    // use toFixed to cut off decimal places without rounding
    const shortValue = Number(value.toFixed(2));

    newValue = shortValue;

    // if the newValue is not a whole number, return it with two decimal places and a suffix, otherwise return it as a whole number with a suffix
    return newValue % 1 !== 0
      ? newValue.toFixed(2) + suffixes[suffixNum]
      : `${newValue}${suffixes[suffixNum]}`;
  }

  // if the value is less than 1000, return the original value as a string
  return `${newValue}`;
};

// Fallback in case of error when chart renders. h-2/3 makes the text content feel centered within the card due to the gap on the container.
// Hopefully we'll never see this!
const ChartErrorFallback = () => (
  <div className="flex h-2/3 items-center justify-center">
    <span className="text-gray-400 text-sm">
      Could not load chart, please try again later.
    </span>
  </div>
);

interface ChartProps {
  margin?: Margin | null;
  isMarginLoading?: boolean;
}

export const Chart = ({ margin, isMarginLoading }: ChartProps) => {
  // get references to the chart container, the parent context, and the active chart item
  const ref =
    useRef<ChartDotJS<"scatter", { x: Date; y: number }[], unknown>>(null);
  const [isDeleteAccountModalOpen, setIsDeleteAccountModalOpen] =
    useState(false);
  const { product, paperAccounts, currentAccount } = useAccountContext();

  // set the default period to "1D" and get the chart data for that period
  const [period, setPeriod] = useState<Timeframe>("1D");

  // track manual refresh to show spinner only when user clicks refresh button
  const [isManualRefresh, setIsManualRefresh] = useState(false);

  const { marketStatus } = useGetMarketStatus();

  // For the 1D chart, use the current session's start from market status
  const overnightStart =
    period === "1D" && marketStatus?.current?.from
      ? marketStatus.current.from
      : undefined;

  const {
    portfolio,
    isLoading: isPortfolioLoading,
    refetch: refetchPortfolio,
    isFetching: isPortfolioFetching,
  } = useGetPortfolioHistory({
    accountID: currentAccount?.id ?? "",
    product: product ?? "",
    intraday_reporting: period === "1D" ? "continuous" : "extended_hours",
    period,
    ...(overnightStart && { start: overnightStart }),
    options: {
      enabled: !!currentAccount,
    },
  });

  // v5 removed query onSuccess; clear the manual-refresh flag once the
  // (re)fetch settles so the refresh spinner doesn't stick.
  useEffect(() => {
    if (isManualRefresh && !isPortfolioFetching) {
      setIsManualRefresh(false);
    }
  }, [isManualRefresh, isPortfolioFetching]);

  const topBuffer = 10;
  const bottomBuffer = 10;

  // Build dates, equities and {x, y} points from a SINGLE aligned source so the
  // two portfolio-history arrays can never drift out of alignment (which is what
  // produced the Chart.js "reading 'x'" crash). See ./utils for details.
  const { dates, equities, points, maxY, minY } = buildChartData(
    portfolio?.timestamp ?? [],
    portfolio?.equity ?? [],
    { topBuffer, bottomBuffer },
  );

  const isLoaded =
    // check if the trade account data is loaded
    !!currentAccount?.equity && !isPortfolioLoading && !isMarginLoading;

  const denormalizeEquityValue = (v: number) =>
    denormalizeEquity(v, minY, maxY, { topBuffer, bottomBuffer });

  // format the chart data for ChartJS2
  const data = {
    datasets: [
      {
        // disable animation to reduce chart complexity
        animation: false,
        // fill the area under the chart with a solid color
        fill: { value: 0 },
        // do not show data points
        pointRadius: 0,
        // set the width of the chart line
        borderWidth: 2.5,
        // set the border radius of the chart line
        borderRadius: 1,
        // disable data parsing for performance
        parsing: false,
        // show the chart line
        showLine: true,
        // enable line gaps if any data points are missing
        spanGaps: true,
        // normalize the data to fit in the chart
        normalized: true,
        // interpolate data between points
        interpolate: true,
        tension: 0.4,
        borderColor: "#f7c911",
        backgroundColor: (context: ScriptableContext<"scatter">) => {
          const chart = context.chart;
          const ctx = chart.ctx;
          const gradient = ctx.createLinearGradient(0, 0, 0, chart.height);

          gradient.addColorStop(0, `#f7c91150`);
          gradient.addColorStop(0.75, `#f7c91100`);

          return gradient;
        },
        // format the data as an array of x,y values (already aligned & valid)
        data: points,
        // disable the hover effect on data points
        hoverRadius: 0,
        // associate the data with the y-axis
        yAxisID: "y",
      },
    ],
  };

  // set the time unit based on the period
  const timeUnit = period === "1D" ? "hour" : "day";

  // format the chart options for ChartJS2
  const options = {
    // set options for chart appearance and behavior
    dates,
    equities,
    period,
    // set the animation duration in milliseconds
    animation: {
      duration: 0,
    },
    // disable animations
    animations: false,
    // disable maintaining aspect ratio when resizing the chart
    maintainAspectRatio: false,
    // disable animation when resizing the chart
    responsiveAnimationDuration: 0,
    // disable intersection mode
    interaction: {
      intersect: false,
      // set the interaction mode to index
      mode: "index",
    },
    scales: {
      x: {
        // set the minimum value for the x-axis
        min: dates[0],
        // set the maximum value for the x-axis
        max: dates[dates.length - 1],
        time: {
          // set the time unit for the x-axis
          unit: timeUnit,
          displayFormats: {
            // format for hours
            hour: "h A",
            // format for days
            day: "MMM D",
          },
          // set the minimum unit for the x-axis
          minUnit: "hour",
        },
        // enable displaying the x-axis
        display: true,
        grid: {
          // set the color for the x-axis grid lines
          color: "#A1A1AA20",
          // set the dash style for the x-axis grid lines
          borderDash: [4, 4],
          // enable displaying the x-axis grid lines
          display: false,
          // disable drawing a border around the chart
          drawBorder: false,
        },
        ticks: {
          // set the color for the x-axis tick marks
          color: "#A1A1AA",
          display: true,
          maxTicksLimit: 5,
          // set the padding for the x-axis tick marks
          padding: 10,
          font: {
            family: "Inter var",
            // set the font weight for the x-axis tick labels
            weight: 500,
          },
          // set the bounds for the x-axis tick labels
          bounds: "labels",
          // enable automatic skipping of x-axis tick labels
          autoSkip: true,
          callback: (value: number | string) => {
            let format: string;
            switch (period) {
              case "1D":
                format = "h A";
                break;
              case "all":
                format = "MMM D, YYYY";
                break;
              default:
                format = "MMM D";
            }

            // set the format for x-axis tick labels
            return moment(value).format(format);
          },
        },
      },
      y: {
        // enable displaying the y-axis
        display: true,
        min: 0,
        max: 100,
        ticks: {
          // set the padding for the y-axis tick marks
          padding: 10,
          maxTicksLimit: 5,
          // enable automatic skipping of y-axis tick labels
          display: true,
          // set the color for the y-axis tick marks
          color: "#A1A1AA",
          autoSkip: true,
          font: {
            family: "Inter var",
            // set the font weight for the y-axis tick labels
            weight: 500,
          },
          callback: (value: number | string) => {
            // For flat data the denormalized value is a constant, so show it
            // directly. For ranged data use the denormalized value (clamped at
            // 0). A falsy check here would wrongly treat a legitimate $0 tick
            // as missing and fall back to minY.
            const displayValue =
              maxY === minY
                ? maxY
                : Math.max(denormalizeEquityValue(Number(value)), 0);
            return `$${abbreviateNumber(displayValue)}`;
          },
        },
        grid: {
          // set the color for the y-axis grid lines
          color: "#A1A1AA20",
          // set the dash style for the y-axis grid lines
          borderDash: [4, 4],
          // enable displaying the y-axis grid lines
          display: true,
          // disable drawing a border around the chart
          drawBorder: false,
        },
      },
    },
    // set options for chart plugins
    plugins: {
      // hide the chart legend
      legend: {
        display: false,
      },
      // enable data decimation to improve performance for large datasets
      decimation: {
        enabled: true,
      },
      // propagate the last defined value to fill any gaps in the chart data
      filler: {
        propagate: true,
      },
      // enable a crosshair that follows the user's cursor and displays data points on the chart
      crosshair: {
        enabled: true,
      },

      // set options for the chart tooltip
      tooltip: {
        // enable the tooltip
        enabled: true,
        // set the position of the tooltip relative to the data point
        position: "nearest",
        // use a dot for the tooltip label instead of a rectangle
        usePointStyle: true,
        // set the background color of the tooltip
        backgroundColor: "transparent",
        // set the font color of the tooltip title
        titleColor: "transparent",
        // set the font color of the tooltip footer
        footerColor: "transparent",
        // set the font color of the tooltip body
        bodyColor: "transparent",
        // hide the color blocks in the tooltip
        displayColors: false,
        // set the border width of the tooltip
        borderWidth: 0,
        // set the padding between the tooltip caret and the tooltip body
        caretPadding: 0,
        // set the size of the tooltip caret
        caretSize: 0,
      },

      zoom: {
        zoom: {
          drag: {
            enabled: true,
          },
          pinch: {
            enabled: true,
          },
          mode: "x",
        },
      },
    },
  };

  const isPaperAccountDeletable =
    product === "paper" && paperAccounts.length > 1;

  return (
    <>
      <Card
        title="Your portfolio"
        tools={
          <div className="flex flex-wrap items-center gap-2">
            {TIMEFRAME_PERIODS.map((option) => (
              <Badge
                key={option.value}
                variant={period !== option.value ? "ghost" : "gray"}
                onClick={() => {
                  if (period !== option.value) {
                    setIsManualRefresh(true);
                    setPeriod(option.value);
                  }
                }}
                className="cursor-pointer text-gray-600 text-sm"
              >
                {option.label}
              </Badge>
            ))}
            <Button
              variant="ghost"
              className="mx-2 flex h-8 w-8 items-center justify-center rounded-full bg-transparent p-0 transition-colors hover:bg-gray-100"
              onClick={() => {
                setIsManualRefresh(true);
                refetchPortfolio();
              }}
              type="button"
              aria-label="Refresh portfolio data"
            >
              <ArrowClockwiseIcon
                size={20}
                weight="bold"
                className="text-gray-600 hover:text-gray-800"
              />
            </Button>
            {isPaperAccountDeletable && (
              <Button
                variant="secondary"
                onClick={() => setIsDeleteAccountModalOpen(true)}
              >
                Delete Account
              </Button>
            )}
          </div>
        }
      >
        <div className="mb-8 flex items-start justify-between">
          <Snapshot
            accountID={currentAccount?.id}
            equity={margin?.equity}
            lastEquity={margin?.last_equity}
            denormalize={denormalizeEquityValue}
          />
        </div>
        <div className="relative h-56">
          <div
            className={clsx(
              "absolute inset-0",
              isLoaded
                ? "opacity-100 transition-opacity duration-300"
                : "opacity-0",
            )}
          >
            <ErrorBoundary errorComponent={ChartErrorFallback}>
              <ChartJS2
                id="chart"
                type="scatter"
                className="content max-h-56"
                ref={ref}
                // @ts-expect-error type hell
                data={data}
                // @ts-expect-error type hell
                options={options}
                plugins={[Crosshair]}
              />
            </ErrorBoundary>
          </div>
          <div
            className={clsx(
              "absolute inset-0 flex items-center justify-center",
              isLoaded ? "pointer-events-none opacity-0" : "opacity-100",
            )}
          >
            <Spinner className="mx-auto" />
          </div>
        </div>
      </Card>

      {isDeleteAccountModalOpen && (
        <DeleteAccountModal
          isOpen
          currentAccountId={currentAccount?.id}
          onClose={() => setIsDeleteAccountModalOpen(false)}
        />
      )}
    </>
  );
};

export default Chart;
